import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Info, Server, Code, Database, Monitor, Package } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SystemInfo = () => {
  const [systemInfo, setSystemInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSystemInfo();
  }, []);

  const fetchSystemInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/system/info`);
      if (response.data.success) {
        setSystemInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching system info:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!systemInfo) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-slate-500 dark:text-slate-400">Gagal memuat informasi sistem</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-fade-in p-4 w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-200">Informasi Sistem</h1>
          <p className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">Detail aplikasi dan teknologi yang digunakan</p>
        </div>
        <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white text-xs py-1 px-3">
          {systemInfo.application.full_name}
        </Badge>
      </div>

      {/* Application Info Card */}
      <Card className="border-0 shadow-md bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
        <CardHeader className="py-2 px-3">
          <CardTitle className="flex items-center text-sm">
            <Info className="w-4 h-4 mr-1.5" />
            Informasi Aplikasi
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-3 px-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] opacity-80 mb-0.5">Nama Aplikasi</p>
              <p className="text-base font-bold">{systemInfo.application.name}</p>
            </div>
            <div>
              <p className="text-[10px] opacity-80 mb-0.5">Versi</p>
              <p className="text-base font-bold">Ver. {systemInfo.application.version}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] opacity-80 mb-0.5">Deskripsi</p>
              <p className="text-xs">{systemInfo.application.description}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Backend Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="py-2 px-3 border-b">
            <CardTitle className="flex items-center text-xs font-semibold">
              <Server className="w-3.5 h-3.5 mr-1.5 text-indigo-600 dark:text-indigo-400" />
              Backend (Server)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded dark:bg-slate-900">
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Framework</p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{systemInfo.backend.framework}</p>
                </div>
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] py-0.5 px-1.5 dark:bg-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-900/40">
                  v{systemInfo.backend.version}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded dark:bg-slate-900">
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Bahasa Pemrograman</p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{systemInfo.backend.language}</p>
                </div>
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px] py-0.5 px-1.5 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/40">
                  v{systemInfo.backend.language_version}
                </Badge>
              </div>
              
              <div className="p-2 bg-slate-50 rounded dark:bg-slate-900">
                <p className="text-[10px] text-slate-500 mb-0.5 dark:text-slate-400">Platform</p>
                <p className="text-[10px] text-slate-700 font-mono dark:text-slate-200">{systemInfo.backend.platform}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Frontend Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="py-2 px-3 border-b">
            <CardTitle className="flex items-center text-xs font-semibold">
              <Monitor className="w-3.5 h-3.5 mr-1.5 text-indigo-600 dark:text-indigo-400" />
              Frontend (Client)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded dark:bg-slate-900">
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Framework</p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{systemInfo.frontend.framework}</p>
                </div>
                <Badge className="bg-cyan-100 text-cyan-700 hover:bg-cyan-100 text-[10px] py-0.5 px-1.5 dark:bg-cyan-900/40 dark:text-cyan-300 dark:hover:bg-cyan-900/40">
                  v{systemInfo.frontend.version}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded dark:bg-slate-900">
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Bahasa Pemrograman</p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{systemInfo.frontend.language}</p>
                </div>
                <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100 text-[10px] py-0.5 px-1.5 dark:bg-yellow-900/40 dark:text-yellow-300 dark:hover:bg-yellow-900/40">
                  ES6+
                </Badge>
              </div>
              
              <div className="p-2 bg-slate-50 rounded dark:bg-slate-900">
                <p className="text-[10px] text-slate-500 mb-0.5 dark:text-slate-400">UI Library</p>
                <p className="text-[10px] text-slate-700 font-semibold dark:text-slate-200">{systemInfo.frontend.ui_library}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Database Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="py-2 px-3 border-b">
            <CardTitle className="flex items-center text-xs font-semibold">
              <Database className="w-3.5 h-3.5 mr-1.5 text-indigo-600 dark:text-indigo-400" />
              Database
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded dark:bg-slate-900">
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Tipe Database</p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{systemInfo.database.type}</p>
                </div>
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[10px] py-0.5 px-1.5 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-900/40">
                  {systemInfo.database.version}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded dark:bg-slate-900">
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Driver</p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{systemInfo.database.driver}</p>
                </div>
                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-[10px] py-0.5 px-1.5 dark:bg-purple-900/40 dark:text-purple-300 dark:hover:bg-purple-900/40">
                  v{systemInfo.database.driver_version}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Server Info */}
        {systemInfo.server && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="py-2 px-3 border-b">
            <CardTitle className="flex items-center text-xs font-semibold">
              <Server className="w-3.5 h-3.5 mr-1.5 text-indigo-600 dark:text-indigo-400" />
              Server
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded dark:bg-slate-900">
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Tipe Server</p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{systemInfo.server.type}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded dark:bg-slate-900">
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">IP Address</p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{systemInfo.server.ip}</p>
                </div>
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px] py-0.5 px-1.5 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/40">
                  {systemInfo.server.location}
                </Badge>
              </div>
              
              <div className="p-2 bg-slate-50 rounded dark:bg-slate-900">
                <p className="text-[10px] text-slate-500 mb-0.5 dark:text-slate-400">Provider</p>
                <p className="text-[10px] text-slate-700 font-semibold dark:text-slate-200">{systemInfo.server.provider}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* System Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="py-2 px-3 border-b">
            <CardTitle className="flex items-center text-xs font-semibold">
              <Package className="w-3.5 h-3.5 mr-1.5 text-indigo-600 dark:text-indigo-400" />
              Sistem Operasi
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded dark:bg-slate-900">
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Operating System</p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{systemInfo.system.os}</p>
                </div>
                <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-[10px] py-0.5 px-1.5 dark:bg-orange-900/40 dark:text-orange-300 dark:hover:bg-orange-900/40">
                  {systemInfo.system.os_version}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded dark:bg-slate-900">
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Architecture</p>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{systemInfo.system.architecture}</p>
                </div>
              </div>
              
              <div className="p-2 bg-slate-50 rounded dark:bg-slate-900">
                <p className="text-[10px] text-slate-500 mb-0.5 dark:text-slate-400">Python Implementation</p>
                <p className="text-[10px] text-slate-700 font-semibold dark:text-slate-200">{systemInfo.system.python_implementation}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tech Stack Summary */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-slate-100">
        <CardHeader className="py-2 px-3">
          <CardTitle className="text-xs font-semibold">Tech Stack Summary</CardTitle>
        </CardHeader>
        <CardContent className="pb-3 px-3">
          <div className="flex flex-wrap gap-1.5">
            <Badge className="bg-indigo-600 text-white hover:bg-indigo-600 text-[10px] py-0.5 px-2">
              FastAPI {systemInfo.backend.version}
            </Badge>
            <Badge className="bg-blue-600 text-white hover:bg-blue-600 text-[10px] py-0.5 px-2">
              Python {systemInfo.backend.language_version}
            </Badge>
            <Badge className="bg-cyan-600 text-white hover:bg-cyan-600 text-[10px] py-0.5 px-2">
              React {systemInfo.frontend.version}
            </Badge>
            <Badge className="bg-yellow-600 text-white hover:bg-yellow-600 text-[10px] py-0.5 px-2">
              JavaScript ES6+
            </Badge>
            <Badge className="bg-green-600 text-white hover:bg-green-600 text-[10px] py-0.5 px-2">
              {systemInfo.database.type}
            </Badge>
            <Badge className="bg-purple-600 text-white hover:bg-purple-600 text-[10px] py-0.5 px-2">
              TailwindCSS
            </Badge>
            <Badge className="bg-pink-600 text-white hover:bg-pink-600 text-[10px] py-0.5 px-2">
              Shadcn/ui
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center py-2">
        <p className="text-[10px] text-slate-500 dark:text-slate-400">
          © 2025 {systemInfo.application.name}. Built with ❤️ using modern web technologies.
        </p>
      </div>
    </div>
  );
};

export default SystemInfo;
